// A stored (or half-edited) rate card turned into the shape the pricing function needs. Shared by
// the office (Settings, New shipment) and the ops phone (pickup intake), so a price is the same
// everywhere (03 §3.3).
import {
  isCurrencyCode,
  type DestinationKind,
  type PricingAddOn,
  type PricingCard,
  type PricingRule,
} from '@ecobox/domain';

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/**
 * Pricing needs complete rules only. A card being edited may have half-filled rows; those are
 * left out here so the test panel can still price with the rest (the form shows what is missing).
 */
export function toPricingCard(source: Record<string, unknown>): PricingCard | undefined {
  const currency = source['currency'];
  const service = source['service'];
  if (
    !isCurrencyCode(currency) ||
    (service !== 'sea' && service !== 'air' && service !== 'prime')
  ) {
    return undefined;
  }
  const rules: PricingRule[] = [];
  for (const raw of Array.isArray(source['rules'])
    ? (source['rules'] as Record<string, unknown>[])
    : []) {
    const rateMinor = num(raw['rateMinor']);
    const basis = raw['basis'];
    if (
      rateMinor === undefined ||
      (basis !== 'per_box' && basis !== 'per_kg' && basis !== 'per_cbm')
    )
      continue;
    const dest = raw['destination'] as { kind?: unknown; value?: unknown } | undefined;
    const kind = dest?.kind;
    const hasKind = typeof kind === 'string' && kind !== '';
    const hasDest = hasKind && typeof dest?.value === 'string' && dest.value !== '';
    // A destination type with no place chosen yet is unfinished: leave the rule out.
    if (hasKind && !hasDest) continue;
    const minCharge = num(raw['minChargeMinor']);
    const divisor = num(raw['volumetricDivisor']);
    const minKg = num(raw['minKg']);
    const maxKg = num(raw['maxKg']);
    const minChargeableKg = num(raw['minChargeableKg']);
    rules.push({
      id: typeof raw['id'] === 'string' ? raw['id'] : `r${rules.length + 1}`,
      ...(hasDest
        ? { destination: { kind: kind as DestinationKind, value: String(dest?.value) } }
        : {}),
      ...(typeof raw['sizeId'] === 'string' && raw['sizeId'] !== ''
        ? { sizeId: raw['sizeId'] }
        : {}),
      basis,
      rateMinor,
      ...(minCharge !== undefined ? { minChargeMinor: minCharge } : {}),
      ...(divisor !== undefined ? { volumetricDivisor: divisor } : {}),
      ...(minKg !== undefined ? { minKg } : {}),
      ...(maxKg !== undefined ? { maxKg } : {}),
      ...(minChargeableKg !== undefined ? { minChargeableKg } : {}),
    });
  }
  const addOns: PricingAddOn[] = [];
  for (const raw of Array.isArray(source['addOns'])
    ? (source['addOns'] as Record<string, unknown>[])
    : []) {
    const value = num(raw['value']);
    const kind = raw['kind'];
    if (
      typeof raw['code'] !== 'string' ||
      raw['code'] === '' ||
      value === undefined ||
      (kind !== 'percent_of_declared' && kind !== 'per_shipment' && kind !== 'per_box')
    ) {
      continue;
    }
    const minCharge = num(raw['minChargeMinor']);
    addOns.push({
      code: raw['code'],
      label: typeof raw['label'] === 'string' && raw['label'] !== '' ? raw['label'] : raw['code'],
      kind,
      value,
      ...(minCharge !== undefined ? { minChargeMinor: minCharge } : {}),
    });
  }
  const minimum = num(source['minimumChargeMinor']);
  return {
    id: typeof source['id'] === 'string' && source['id'] !== '' ? source['id'] : 'rc_DRAFT_new',
    version: num(source['version']) ?? 1,
    service,
    currency,
    rules,
    addOns,
    ...(minimum !== undefined ? { minimumChargeMinor: minimum } : {}),
  };
}
